import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@/fontawesome';
import { formatCurrencyCentValue } from '@/utils/helpers';
import type { CurrencyCent } from '@/models/types';
import type { Percentage } from '@/models/base-types';

interface TradeResultPanelProps {
  averageBuyPrice: CurrencyCent;
  averageExitPrice: CurrencyCent | null;
  // `yield` is a reserved word, hence the suffix
  yieldValue: Percentage;
  currency: string;
  className?: string;
}

/**
 * Result panel for finished Swing and Long-Term trades: Ø-Einstieg → Ø-Ausstieg plus the yield.
 * Used by the list cards (mobile detail view, passed `lg:hidden`) and by the shareable image card.
 * Contains no responsive variants itself so it renders identically at any viewport width.
 */
export default function TradeResultPanel({
  averageBuyPrice,
  averageExitPrice,
  yieldValue,
  currency,
  className = ''
}: TradeResultPanelProps) {
  const isProfit = yieldValue >= 0;

  return (
    <div className={`rounded-xl border p-4 bg-gradient-to-br ${
      isProfit
        ? 'border-green-200 from-green-50 to-emerald-100/60 dark:border-green-800/60 dark:from-green-900/30 dark:to-emerald-900/10'
        : 'border-red-200 from-red-50 to-rose-100/60 dark:border-red-800/60 dark:from-red-900/30 dark:to-rose-900/10'
    } ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Einstieg</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrencyCentValue(averageBuyPrice, currency)}
          </div>
        </div>
        <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" />
        <div className="min-w-0 text-right">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ø-Ausstieg</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {averageExitPrice !== null ? formatCurrencyCentValue(averageExitPrice, currency) : '-'}
          </div>
        </div>
      </div>
      <div className={`mt-3 -mx-4 -mb-4 border-t rounded-b-xl overflow-hidden ${
        isProfit ? 'border-green-200/70 dark:border-green-800/40' : 'border-red-200/70 dark:border-red-800/40'
      }`}>
        <div className="px-4 py-3 text-center">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Rendite</div>
          <div className={`text-3xl font-extrabold tracking-tight ${
            isProfit
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          }`}>
            {isProfit ? '+' : ''}{(yieldValue * 100).toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
}
